import { Injectable } from '@angular/core';
import { PlaybackMode } from 'fl-content-authorization';
import {
  AdPlayer,
  PLAYBACK_STATES,
  Player,
  SEEKING_STATES,
} from 'fl-player-interface';
import { LanguageService } from 'src/app/language.service';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { LocalStorageService } from 'src/app/shared/services/storage/local-storage.service';
import { SessionStorageService } from 'src/app/shared/services/storage/session-storage.service';
import { KEYS } from 'src/app/shared/utils/constants/storage.constants';
import { ErrorEvents } from 'src/app/shared/utils/ErrorUtils';
import { getErrorData, removeUndefined } from 'src/app/shared/utils/EventUtlis';
import { getAdInfo } from 'src/app/shared/utils/PlaybackUtils';
import { isFreeMovie, showName } from 'src/app/shared/utils/playerUtils';
import { ClevertapEvent } from '../enum/clevertap.eventType';
import { ClevertapBaseStore } from '../store/clevertap-base.store';
import { ClevertapContentStore } from '../store/clevertap-content.store';
import { ClevertapPlayerStore } from '../store/clevertap-player.store';

@Injectable({
  providedIn: 'root',
})
export class ClevertapService {
  private playbackMode: string;
  private seekStartTime: number;
  private seekEndTime: number;
  private isStreamEnd: boolean;
  private triggeredMilestones: Set<number> = new Set();
  private adClickUrl: string;
  private lastWatchedEventKey: string = ''; // Track last Watched event signature to prevent duplicates

  constructor(
    private readonly baseStore: ClevertapBaseStore,
    private readonly contentStore: ClevertapContentStore,
    private readonly playerStore: ClevertapPlayerStore,
    private readonly localStorage: LocalStorageService,
    private readonly sessionStorage: SessionStorageService,
    private readonly authService: AuthService,
    private readonly languageService: LanguageService
  ) {}

  public addEvent(eventName: ClevertapEvent, customAttributes?: any): void {
    try {
      const { resource, planData, ...custom } = customAttributes || {};
      if (resource) {
        this.contentStore.setContentDetails(customAttributes.resource);
      }
      let attributes: any = {
        ...this.getBaseAttributes(eventName),
      };
      if (planData) {
        const { planData: plandata } = customAttributes;
        attributes = {
          ...attributes,
          ...this.baseStore.getBaseAttributesByPlanData(plandata, eventName),
        };
      }
      if (custom) {
        attributes = {
          ...attributes,
          ...custom,
        };
      }

      this.sendEventToClevertap(eventName, attributes);
    } catch (e) {
      console.error(e);
    }
  }

  // NOTE: Do not Remove the below commented out code it may be used in future.

  // /**
  //  * Update the user profile on clevertap
  //  *
  //  * @param contentLanguage
  //  * @param displayLanguage
  //  */
  // profileSet(contactId: string, name: string, contentLanguage: string, displayLanguage: string): void {
  //   if (!window.clevertap) return;
  //   try {
  //     window.clevertap.profile.push({
  //       Site: {
  //         // eslint-disable-next-line @typescript-eslint/naming-convention
  //         Identity: contactId,
  //         // eslint-disable-next-line @typescript-eslint/naming-convention
  //         Name: name,
  //         // eslint-disable-next-line @typescript-eslint/naming-convention
  //         ContentLanguage: contentLanguage,
  //         // eslint-disable-next-line @typescript-eslint/naming-convention
  //         DisplayLanguage: displayLanguage,
  //       },
  //     });
  //   } catch (error) {
  //     console.log('ERROR CLEVERTAP', error);
  //   }
  // }
  private getBaseAttributes(eventName: ClevertapEvent): any {
    let attributes: any = {
      ...this.baseStore.getLanguageInfo(),
      ...this.baseStore.getDemographyInfo(),
      ...this.baseStore.getBaseAttributesByEvent(eventName),
    };

    if (this.contentStore.getContentDetails()) {
      attributes = {
        ...attributes,
        ...this.contentStore.getContentInfoByEvent(eventName),
      };
    }

    // console.log('attributes', attributes);

    return attributes;
  }

  loginUser(
    contactId: string,
    name: string,
    email: string,
    phone: string
  ): void {
    if (!window.clevertap) return;
    try {
      const subscriptionStatus = this.localStorage.getItem(
        KEYS.SUBSCRIPTION_STATUS
      );
      const mainContact = JSON.parse(
        this.sessionStorage.getItem(KEYS.MAIN_CONTACT)
      );
      const ahaUserType =
        this.sessionStorage.getItem(KEYS.AHA_USER_TYPE) || undefined;
      const userType = this.contentStore.getUserSubscriptionStatus(ahaUserType);

      window.clevertap.onUserLogin.push({
        Site: {
          Name: name,
          Identity: contactId,
          Email: email,
          parentalControl: mainContact?.ageGroup,
          userStatus: userType,
          phone,
          subscriptionStatus,
          'MSG-email': true,
          'MSG-push': false,
        },
      });
    } catch (error) {
      console.warn('ERROR CLEVERTAP', error);
    }
  }

  private sendEventToClevertap(
    eventName: ClevertapEvent,
    attributes: any
  ): void {
    try {
      // Will trigger only for logged in users
      if (this.authService.isLoggedIn()) {
        const cleanAttr = removeUndefined(attributes);
        // console.log('[CLEVERTAP EVENT] : ', eventName, cleanAttr);
        if (window.clevertap) {
          window.clevertap.event.push(eventName, cleanAttr);
        }
      }
    } catch (err) {
      console.warn('ERROR CLEVERTAP: ', err);
    }
  }

  public attachPlayer(
    player: Player,
    playbackMode: PlaybackMode,
    playerConfig?: any,
    isAdEnabled: boolean = false
  ): void {
    this.detachPlayer(isAdEnabled);
    this.adClickUrl = playerConfig?.adClickUrl;
    this.playerStore.setPlayer(player);
    this.playerStore.setPlayerConfig(playerConfig);
    this.playbackMode = playbackMode || undefined;
    this.subscribeToInternalPlayerEvents(player);
    if (isAdEnabled) {
      this.subscribeAdPlayerEvents(player as AdPlayer);
    }
  }

  public detachPlayer(isAdEnabled: boolean = false): void {
    try {
      const player = this.playerStore.getPlayer();
      if (player) {
        this.unsubscribeFromInternalPlayerEvents(player);
        if (isAdEnabled) {
          this.unsubscribeAdPlayerEvents(player as AdPlayer);
        }
        this.playerStore.setPlayer(undefined);
      }
      this.contentStore.setContentDetails(undefined);
      this.seekEndTime = null;
      this.seekStartTime = null;
      this.triggeredMilestones.clear();
      this.lastWatchedEventKey = ''; // Reset event key when player is reset
    } catch (e) {
      console.warn(e);
    }
  }

  private subscribeToInternalPlayerEvents = (player?: Player): void => {
    if (!player) {
      return;
    }
    player.subscribe('contentloaded', this.onPlayerContentLoaded);
    player.subscribe('progressupdate', this.onPlayerProgressUpdate);
    player.subscribe('playbackstatechanged', this.onPlaybackStateChanged);
    player.subscribe('streamended', this.onPlayerStreamEnded);
    player.subscribe('error', this.onPlayerError);
    player.subscribe('seekingstatechanged', this.onSeekingStateChanged);
  };

  private subscribeAdPlayerEvents(adPlayer: AdPlayer): void {
    if (!adPlayer) {
      return;
    }
    adPlayer.subscribe('adstart', this.onAdStart);
    adPlayer.subscribe('adbreakstart', this.onAdBreakStart);
    adPlayer.subscribe('adbreakend', this.onAdBreakEnd);
    adPlayer.subscribe('adend', this.onAdEnd);
    adPlayer.subscribe('adverttrackingevent', this.onAdTracking);
  }

  private unsubscribeFromInternalPlayerEvents = (player?: Player): void => {
    if (!player) {
      return;
    }
    try {
      player.unsubscribe('contentloaded', this.onPlayerContentLoaded);
      player.unsubscribe('progressupdate', this.onPlayerProgressUpdate);
      player.unsubscribe('playbackstatechanged', this.onPlaybackStateChanged);
      player.unsubscribe('streamended', this.onPlayerStreamEnded);
      player.unsubscribe('error', this.onPlayerError);
      player.unsubscribe('seekingstatechanged', this.onSeekingStateChanged);
    } catch {}
  };

  private unsubscribeAdPlayerEvents(player: AdPlayer): void {
    player.unsubscribe('adstart', this.onAdStart); // Use the stored reference
    player.unsubscribe('adbreakstart', this.onAdBreakStart);
    player.unsubscribe('adbreakend', this.onAdBreakEnd);
    player.unsubscribe('adend', this.onAdEnd); // Use the stored reference
    player.unsubscribe('adverttrackingevent', this.onAdTracking);
  }

  public onPlayerError = (error: any): void => {
    const playErrorEventData = getErrorData(error, ErrorEvents.PLAYBACK_ERROR);
    /*   if (!this.playerStore.getPlayer()) {
      return;
    } */

    let attributes: any = {
      ...this.getBaseAttributes(ClevertapEvent.MEDIA_ERROR),
    };
    attributes = {
      ...attributes,
      errorMessage: error?.errorMessage,
      contentTitle:
        this.contentStore.getContentDetails()?.title[
          this.languageService.currLang
        ],
      isFree: isFreeMovie(this.contentStore.getContentDetails()),
      componentErrorCode:
        playErrorEventData?.errorDescription?.componentErrorCode ||
        playErrorEventData.componentErrorCode,
      errorCode: playErrorEventData?.errorCode,
      hexErrorCode:
        playErrorEventData?.errorDescription?.hexErrorCode ||
        playErrorEventData.hexErrorCode,
      internalErrorMessage: playErrorEventData?.errorDescription?.errorMessage,
      ...this.baseStore.getDemographyInfo(),
    };
    this.sendEventToClevertap(ClevertapEvent.MEDIA_ERROR, attributes);
  };

  private onSeekingStateChanged = (seekingState: SEEKING_STATES): void => {
    const playerInstance = this.playerStore.getPlayer();
    switch (seekingState) {
      case SEEKING_STATES.ACTIVE: {
        this.seekStartTime = playerInstance.currentTime || 0;
        break;
      }
      case SEEKING_STATES.INACTIVE: {
        this.seekEndTime = playerInstance.currentTime || 0;
        break;
      }
    }
  };

  private onPlayerContentLoaded = (): void => {
    if (!this.playerStore.getPlayer()) {
      return;
    }
    // Removed: Don't send WATCHED event at 0% progress
    // this.sendWatchedEvent();
  };

  private onPlayerProgressUpdate = (): void => {
    const playerInstance = this.playerStore.getPlayer();
    if (!playerInstance) return;

    const duration = playerInstance.duration || 0;
    if (duration <= 0) return;

    const currentTime = playerInstance.currentTime || 0;
    const progressRatio = currentTime / duration;

    // Define milestones in ascending order
    const milestones = [0.25, 0.5, 0.8, 1.0];

    // Find the highest milestone that should be triggered
    let milestoneToTrigger = null;

    for (const milestone of milestones) {
      if (
        progressRatio >= milestone &&
        !this.triggeredMilestones.has(milestone)
      ) {
        milestoneToTrigger = milestone;
      }
    }

    // Trigger the highest milestone and mark all lower ones as triggered
    if (milestoneToTrigger !== null) {
      for (const milestone of milestones) {
        if (milestone <= milestoneToTrigger) {
          this.triggeredMilestones.add(milestone);
        }
      }
      this.sendWatchedEvent();
    }
  };

  private onPlayerStreamEnded = (): void => {
    this.triggeredMilestones.clear();
    this.isStreamEnd = true;
    if (!this.playerStore.getPlayer()) return;
  };

  private onPlaybackStateChanged = (playbackState: PLAYBACK_STATES): void => {
    if (playbackState === PLAYBACK_STATES.STARTED) {
      if (this.isStreamEnd || !this.triggeredMilestones.has(0)) {
        this.isStreamEnd = false;
        this.triggeredMilestones.add(0);
        this.sendWatchedEvent();
      }
    }
    if (playbackState === PLAYBACK_STATES.IDLE) {
      this.unsubscribeFromInternalPlayerEvents(this.playerStore.getPlayer());
      this.playerStore.setPlayer(undefined);
    }
  };

  private getPlaybackAttributes() {
    const playerInstance = this.playerStore.getPlayer();
    return {
      watchedTime: playerInstance.playbackStatistics?.playTime,
      bufferCount: playerInstance.playbackStatistics?.bufferingTime,
      playbackMode: this.playbackMode,
      contentDuration: this.contentStore.resource?.runningTime || 0,
      seekEndTime: this.seekEndTime,
      seekStartTime: this.seekStartTime,
    };
  }

  private sendWatchedEvent(): void {
    const playerInstance = this.playerStore.getPlayer();
    if (!playerInstance) return;

    // Build attributes first to create unique key
    const attributes = {
      ...this.getBaseAttributes(ClevertapEvent.WATCHED),
      ...this.playerStore.getPlayerInfo(),
      ...this.getPlaybackAttributes(),
      ...this.contentStore.getVODInfo(),
      ...this.baseStore.getDemographyInfo(),
    };

    // Create a unique key from critical attributes that identify the event
    // This prevents sending duplicate events with same content, playTime, etc.
    const eventKey = this.createWatchedEventKey(attributes);

    // Prevent duplicate Watched events with identical key
    if (eventKey === this.lastWatchedEventKey) {
      return;
    }

    // Update the last sent event key
    this.lastWatchedEventKey = eventKey;

    this.sendEventToClevertap(ClevertapEvent.WATCHED, attributes);
  }

  private createWatchedEventKey(attributes: any): string {
    // Create a unique key from critical attributes
    // Include contentID, watchedTime, and contentDuration to identify unique events
    const contentID = attributes.contentID || '';
    const watchedTime = attributes.watchedTime || 0;
    const contentDuration = attributes.contentDuration || '';

    // Round watchedTime to 2 decimal places to handle floating point precision issues
    const watchedTimeRounded = Math.round(watchedTime * 100) / 100;

    // Create key from these critical attributes
    return `${contentID}_${watchedTimeRounded}_${contentDuration}`;
  }

  private onAdStart = (event) => {
    try {
      this.sendEventToClevertap(
        ClevertapEvent.AD_START,
        this.getAdsInformation(event)
      );
    } catch (e) {}
  };

  private onAdBreakStart = (event) => {
    try {
      this.sendEventToClevertap(
        ClevertapEvent.AD_BREAK_START,
        this.getAdsInformation(event)
      );
    } catch (e) {}
  };

  private onAdTracking = (ev): void => {
    switch (ev) {
      case 'Ad Started': // Ad stated: not triggered on 0.52
        break;
      case 'Ad Skipped': // Ad was skipped
        break;
      case 'Ad Click': // Ad LEARN MORE link was clicked
        break;
      case 'Ad Video Clicked': // Ad was clicked
        // Currently there is no event for this case, but there might be
        break;
    }
  };

  private onAdBreakEnd = (event) => {
    try {
      this.sendEventToClevertap(
        ClevertapEvent.AD_BREAK_COMPLETE,
        this.getAdsInformation(event)
      );
    } catch (e) {}
  };

  private onAdEnd = (event) => {
    try {
      this.sendEventToClevertap(
        ClevertapEvent.AD_END,
        this.getAdsInformation(event)
      );
    } catch (e) {}
  };

  private getAdsInformation(event) {
    const currentLang = this.languageService.currLang;
    return {
      ...getAdInfo(event, this.localStorage.getItem('adLinkUrl')),
      ...this.baseStore.getDemographyInfo(),
      ...this.baseStore.getLanguageInfo(),
      ...this.contentStore.getContentInfoByEvent(ClevertapEvent.AD_START),
      ...this.contentStore.getVODInfo(),
      ...this.contentStore.getTrayContentData(),
      playbackMode: this.playbackMode,
      adClickURL: this.adClickUrl || undefined,
      contentDuration: this.contentStore.resource?.runningTime || undefined,
      contentGenre: this.contentStore.resource?.contentGenre?.[currentLang]
        ? this.contentStore.resource?.contentGenre?.[currentLang].join(' , ')
        : undefined,
      contentID: this.contentStore.resource?.id || undefined,
      contentTitle: this.contentStore.resource?.title[currentLang] || undefined,
      directorName: this.contentStore.resource?.director[currentLang] || undefined,
      episodeNumber:
        this.contentStore.resource?.episodeNumber?.toString() || undefined,
      isFree: isFreeMovie(this.contentStore.resource),
      seasonNumber:
        this.contentStore.resource?.seasonNumber?.toString() || undefined,
      showName: showName(this.contentStore.resource, currentLang),
    };
  }
}
